/**
 * HoldingFormPage — 컬렉션 저장 폼 (기획확정안 §6.2 "카드에 저장 <b>가능한</b> 정보").
 *
 * ⚠ **앱 안에서 이 화면으로 보내는 곳이 더는 없다** (2026-09-08, 사용자 결정).
 *   - 등록  `/collections/holdings/new?cardSerialNo=N`   — 담기는 [AddToCollectionSheet] 가 맡는다 (2026-09-03)
 *   - 수정  `/collections/holdings/:holdingSerialNo`     — 보유 목록 행은 **카드 상세**로 간다
 *     (화면정의서 SC-002 `[보유] 카드 → SC-007`). 이 폼이 묻던 구매가·구매일·구매처·
 *     보관장소·메모·인증번호는 2026-08-25 에 폐기된 필드다 — 기능명세 v0.4 에 FN-022 행이
 *     아예 없고, FN-037·FN-072 가 "구매가 입력 기능 없음"을 전제로 쓰여 있다.
 *   라우트와 파일은 남겨 둔다 — 되돌릴 때 근거가 여기 있어야 한다.
 *
 * 왜 갈랐나: 담는 순간에 물을 것은 **수량 하나**다(§6.1 "스캔 결과에서 원하는 카드만
 * 저장"). §6.2 가 나열하는 감정사·구매처·보관장소·메모는 제목 그대로 "저장 **가능한**"
 * 정보이지 담기 전에 받아내야 할 값이 아니다. 한 장 담으려고 여덟 칸짜리 폼을 채우게
 * 하면 스캔해서 쌓는 흐름 자체가 끊긴다.
 *
 * ⚠ 필수는 카드와 수량뿐이다. 구매 정보를 안 적으면 그 카드는 손익 집계에서만 빠진다
 *   (총액·보유 수에는 들어간다).
 * ⚠ 선택 항목에 `isOptional` 을 주지 말 것 — 전역 CSS 가 그것도 필수 별표로 바꾼다
 *   (styles/global.css 의 '필수 표시' 절, 2026-09-02 실사고).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@astryxdesign/core/Button";
import type { ISODateString } from "@astryxdesign/core/Calendar";
import { NumberInput } from "@astryxdesign/core/NumberInput";
import { Selector } from "@astryxdesign/core/Selector";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { Text } from "@astryxdesign/core/Text";
import { TextArea } from "@astryxdesign/core/TextArea";
import TextField from "../../components/primitives/TextField";
import { HStack, VStack } from "@astryxdesign/core/Layout";
import { useMoney } from "../../hooks/useMoney";
import { cardImageUrl, fetchCardsByIds, type CardItem } from "../../api/card";
import {
  createHolding,
  deleteHolding,
  updateHolding,
  uploadHoldingPhoto,
  type HoldingItem,
  type HoldingSaveRequest,
} from "../../api/collection";
import { track } from "../../lib/tracking";
import { COLLECTION_KEY, useCollectionQuery } from "../../hooks/useCollectionQueries";
import { useActivityStore } from "../../store/activityStore";
import { useActivityNavigator } from "../../navigation/useActivityNavigator";
import { useToast } from "../../components/feedback/Toast";
import styles from "./HoldingFormPage.module.css";

/** §5.3·§6.2 — 감정사는 이 3사 + 미등급. 회사별 등급을 하나로 합치지 않는다. */
const GRADERS = ["RAW", "PSA", "BGS", "CGC"] as const;
const GRADES = ["10", "9.5", "9", "8.5", "8", "7", "6", "5", "4", "3", "2", "1"] as const;
const UNGRADED_GRADE = "-";

interface FormState {
  quantity: number;
  purchasePrice: number | null;
  /** DateInput 계약상 YYYY-MM-DD. 미입력은 "" */
  purchaseDate: ISODateString | "";
  purchaseSource: string;
  grader: string;
  grade: string;
  gradeCertNo: string;
  keepPlace: string;
  memo: string;
  photoAttachFileSerialNo: number | null;
  photoUrl: string | null;
}

const EMPTY_FORM: FormState = {
  quantity: 1,
  purchasePrice: null,
  purchaseDate: "",
  purchaseSource: "",
  grader: "RAW",
  grade: UNGRADED_GRADE,
  gradeCertNo: "",
  keepPlace: "",
  memo: "",
  photoAttachFileSerialNo: null,
  photoUrl: null,
};

function toForm(h: HoldingItem): FormState {
  return {
    quantity: h.quantity,
    purchasePrice: h.purchasePrice,
    // BE 는 평범한 string 을 준다 — DateInput 이 요구하는 리터럴 타입으로 좁힌다.
    purchaseDate: (h.purchaseDate ?? "") as ISODateString | "",
    purchaseSource: h.purchaseSource ?? "",
    grader: h.grader,
    grade: h.grade,
    gradeCertNo: h.gradeCertNo ?? "",
    keepPlace: h.keepPlace ?? "",
    memo: h.memo ?? "",
    photoAttachFileSerialNo: h.photoAttachFileSerialNo,
    photoUrl: h.photoUrl,
  };
}

/**
 * @param holdingSerialNo ActivityHost 전용 — activity 안에서는 라우트 매치가 없어
 *   useParams 가 비므로 resolver 가 URL 에서 파싱해 넘긴다 (등록 모드는 undefined).
 *   라우트 렌더에서는 생략.
 */
export default function HoldingFormPage({
  holdingSerialNo: holdingSerialNoProp,
}: { holdingSerialNo?: string } = {}) {
  const { t } = useTranslation("card");
  const money = useMoney();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { holdingSerialNo: paramHoldingSerialNo } = useParams<{ holdingSerialNo: string }>();
  const holdingSerialNo = holdingSerialNoProp ?? paramHoldingSerialNo;
  const [searchParams] = useSearchParams();

  /**
   * 이 폼이 activity(스캔 결과·카드 상세의 "담기")로 열렸는지. 그렇다면 완료 시
   * /collections 로 이탈하지 않고 activity 를 닫아 **아래 화면(스캔 결과)으로 복귀**한다
   * — §6.1 여러 장 연속 담기 흐름. 메인탭 라우팅에서는 종전대로 /collections.
   */
  const inActivity = useActivityStore((s) => {
    const top = s.activities[s.activities.length - 1];
    return top?.kind === "collections/holding";
  });
  const activityNav = useActivityNavigator();
  const finish = useCallback(() => {
    if (inActivity) activityNav.close();
    else navigate("/collections", { replace: true });
  }, [inActivity, activityNav, navigate]);

  const isEdit = holdingSerialNo !== undefined;
  const newCardSerialNo = Number(searchParams.get("cardSerialNo"));

  const { data: collection, isLoading: collectionLoading } = useCollectionQuery();
  const existing = useMemo<HoldingItem | undefined>(() => {
    if (!isEdit) return undefined;
    return collection?.holdings.find((h) => h.holdingSerialNo === Number(holdingSerialNo));
  }, [isEdit, collection, holdingSerialNo]);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [newCard, setNewCard] = useState<CardItem | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 수정 모드는 목록이 도착한 뒤 폼을 채운다.
  useEffect(() => {
    if (existing) setForm(toForm(existing));
  }, [existing]);

  // 등록 모드는 어떤 카드를 담는 중인지 보여주려고 카드 한 장을 조회한다.
  useEffect(() => {
    if (isEdit || !Number.isFinite(newCardSerialNo) || newCardSerialNo <= 0) return;
    let cancelled = false;
    void fetchCardsByIds([newCardSerialNo])
      .then((map) => {
        if (!cancelled) setNewCard(map.get(newCardSerialNo) ?? null);
      })
      .catch(() => {
        /* 카드 정보는 부가 표시다 — 못 불러와도 저장은 된다 */
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit, newCardSerialNo]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handlePhotoPick = useCallback(
    async (file: File | undefined) => {
      if (!file) return;
      setBusy(true);
      try {
        const uploaded = await uploadHoldingPhoto(file);
        setForm((prev) => ({
          ...prev,
          photoAttachFileSerialNo: uploaded.attachFileSerialNo,
          photoUrl: uploaded.photoUrl,
        }));
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t("holding.photoFailed"));
      } finally {
        setBusy(false);
        // 같은 파일을 다시 골라도 change 가 뜨도록 비운다.
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [toast, t],
  );

  const buildRequest = (cardSerialNo: number): HoldingSaveRequest => ({
    cardSerialNo,
    quantity: form.quantity,
    purchasePrice: form.purchasePrice,
    purchaseDate: form.purchaseDate || null,
    purchaseSource: form.purchaseSource || null,
    grader: form.grader,
    // 미등급이면 등급 축을 기본값으로 되돌린다 — 시세 조인 키라 빈 값이 들어가면 안 된다.
    grade: form.grader === "RAW" ? UNGRADED_GRADE : form.grade,
    gradeCertNo: form.grader === "RAW" ? null : form.gradeCertNo || null,
    keepPlace: form.keepPlace || null,
    photoAttachFileSerialNo: form.photoAttachFileSerialNo,
    memo: form.memo || null,
  });

  const handleSave = async () => {
    const cardSerialNo = existing?.card.cardSerialNo ?? newCardSerialNo;
    if (!Number.isFinite(cardSerialNo) || cardSerialNo <= 0) {
      toast.error(t("holding.notFound"));
      return;
    }
    setBusy(true);
    try {
      if (isEdit) {
        await updateHolding(Number(holdingSerialNo), buildRequest(cardSerialNo));
        track("collection_holding_updated", { cardSerialNo });
        toast.success(t("holding.updated"));
      } else {
        await createHolding(buildRequest(cardSerialNo));
        // source 는 경로로 갈린다 — 스캔 결과에서 연 담기(/scan/holdings/new)와
        // 카탈로그에서 연 담기. 요약 일괄 담기는 scan_summary_saved 가 따로 센다.
        // hasPurchasePrice: 구매가는 §6.2 로 NULL 허용이고, 손익 지표의 모수가 이 비중이다.
        track("collection_holding_added", {
          cardSerialNo,
          source: window.location.pathname.startsWith("/scan/") ? "scan" : "catalog",
          hasPurchasePrice: form.purchasePrice !== null && form.purchasePrice !== undefined,
        });
        toast.success(t("holding.saved"));
      }
      await queryClient.invalidateQueries({ queryKey: COLLECTION_KEY });
      finish();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("holding.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit || !window.confirm(t("holding.deleteConfirm"))) return;
    setBusy(true);
    try {
      await deleteHolding(Number(holdingSerialNo));
      track("collection_holding_removed", {
        cardSerialNo: existing?.card.cardSerialNo ?? 0,
      });
      toast.success(t("holding.deleted"));
      await queryClient.invalidateQueries({ queryKey: COLLECTION_KEY });
      finish();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("holding.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  if (isEdit && collectionLoading) {
    return (
      <div className={styles.page}>
        <VStack gap={4}>
          <Skeleton height={120} />
          <Skeleton height={56} />
          <Skeleton height={56} />
        </VStack>
      </div>
    );
  }

  if (isEdit && !existing) {
    return (
      <div className={styles.page}>
        <Text color="secondary">{t("holding.notFound")}</Text>
      </div>
    );
  }

  const card = existing?.card ?? newCard;
  const imageUrl = cardImageUrl(card?.imagePathway);
  const isGraded = form.grader !== "RAW";

  return (
    <div className={styles.page}>
      <VStack gap={6}>
        {/* 어떤 카드를 담는 중인지 — 폼만 있으면 무엇을 저장하는지 알 수 없다 */}
        <div className={styles.cardHeader}>
          {imageUrl ? (
            <img className={styles.cardThumb} src={imageUrl} alt="" loading="lazy" />
          ) : (
            <span className={styles.cardThumbEmpty} aria-hidden="true" />
          )}
          <VStack gap={1}>
            <Text weight="semibold">{card?.name ?? "-"}</Text>
            <Text type="supporting" color="secondary">
              {card
                ? [card.cardsetName, card.number, card.rarity]
                    .filter(Boolean)
                    .join(" · ")
                : ""}
            </Text>
            {existing && existing.currentPrice.amount !== null
              && existing.currentPrice.display !== "NONE" && (
              <Text type="supporting" color="secondary">
                {money.format(existing.currentPrice.amount, existing.currentPrice.currency ?? undefined)}
              </Text>
            )}
          </VStack>
        </div>

        <VStack gap={4}>
          <NumberInput
            label={t("holding.quantity")}
            value={form.quantity}
            onChange={(v) => set("quantity", Math.max(1, Math.floor(v || 1)))}
            min={1}
            max={9999}
            isRequired
          />

          <Selector
            label={t("holding.grader")}
            options={GRADERS.map((g) => ({
              value: g,
              label: g === "RAW" ? t("holding.ungraded") : g,
            }))}
            value={form.grader}
            onChange={(v) => set("grader", v)}
          />

          {/* 등급·인증번호는 등급 카드일 때만 묻는다 (§6.2) */}
          {isGraded && (
            <>
              <Selector
                label={t("holding.grade")}
                options={GRADES.map((g) => ({ value: g, label: g }))}
                value={form.grade === UNGRADED_GRADE ? "" : form.grade}
                onChange={(v) => set("grade", v)}
                placeholder={t("holding.grade")}
              />
              <TextField
                label={t("holding.gradeCertNo")}
                value={form.gradeCertNo}
                onChange={(v) => set("gradeCertNo", v)}
                maxLength={40}
              />
            </>
          )}

          {/* 구매가·구매일 입력은 2026-08-25 에 걷었다 (design · FN-022 폐기 · OI-24).
              컬렉션이 답하는 질문이 "얼마 벌었나" 에서 "지금 얼마인가" 로 바뀌었다 —
              손익은 구매가를 알아야 나오는 값인데 적어 두는 사람이 드물어 대부분
              비어 있었고, 등급·표면이 어긋나면 값 자체가 틀렸다.
              ⚠ 필드는 폼 상태·요청 본문에 **null 로 남는다** — 서버가 아직 그 컬럼을
                들고 있고, 이미 적어 둔 값을 저장할 때마다 지우면 안 된다.
                서버에서 컬럼이 빠질 때 함께 지운다. */}

          <TextField
            label={t("holding.purchaseSource")}
            value={form.purchaseSource}
            onChange={(v) => set("purchaseSource", v)}
            placeholder={t("holding.purchaseSourcePlaceholder")}
            maxLength={100}
          />

          <TextField
            label={t("holding.keepPlace")}
            value={form.keepPlace}
            onChange={(v) => set("keepPlace", v)}
            placeholder={t("holding.keepPlacePlaceholder")}
            maxLength={100}
          />

          {/* 실물사진 — 파일을 먼저 올리고 받은 id 를 저장 요청에 싣는다 */}
          <VStack gap={2}>
            <Text type="supporting" color="secondary">{t("holding.photo")}</Text>
            {form.photoUrl && (
              <img className={styles.photoPreview} src={form.photoUrl} alt="" />
            )}
            <HStack gap={2}>
              <Button
                variant="secondary"
                label={t("holding.photoSelect")}
                isDisabled={busy}
                clickAction={() => fileInputRef.current?.click()}
              />
              {form.photoAttachFileSerialNo !== null && (
                <Button
                  variant="ghost"
                  label={t("holding.photoRemove")}
                  isDisabled={busy}
                  clickAction={() =>
                    setForm((prev) => ({
                      ...prev,
                      photoAttachFileSerialNo: null,
                      photoUrl: null,
                    }))
                  }
                />
              )}
            </HStack>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className={styles.fileInputHidden}
              onChange={(e) => void handlePhotoPick(e.target.files?.[0])}
            />
          </VStack>

          <TextArea
            label={t("holding.memo")}
            value={form.memo}
            onChange={(v) => set("memo", v)}
            placeholder={t("holding.memoPlaceholder")}
            maxLength={500}
          />

          <Text type="supporting" color="secondary">{t("holding.optionalNotice")}</Text>
        </VStack>

        <VStack gap={2}>
          <Button
            variant="primary"
            size="lg"
            label={t("holding.save")}
            isDisabled={busy}
            clickAction={() => void handleSave()}
          />
          {isEdit && (
            <Button
              variant="ghost"
              label={t("holding.delete")}
              isDisabled={busy}
              clickAction={() => void handleDelete()}
            />
          )}
        </VStack>
      </VStack>
    </div>
  );
}
